import { Array, Match, Order, Predicate, Schema } from 'effect';

import { compareTokens } from '../../capability/productListing/compareTokens';

export const FilterCode = Schema.String.check(
  Schema.isPattern(/^(?:[a-z][a-z0-9-]{0,31}|x-(?:[a-f0-9]{2})+)$/)
).pipe(Schema.brand('Listing.FilterCode'));

export type FilterCode = typeof FilterCode.Type;

export const FilterValueToken = Schema.String.check(Schema.isPattern(/^[a-z0-9.-]+$/)).pipe(
  Schema.brand('Listing.FilterValueToken')
);

export type FilterValueToken = typeof FilterValueToken.Type;

export const SortToken = Schema.String.check(Schema.isPattern(/^[a-z0-9-]+$/)).pipe(
  Schema.brand('Listing.SortToken')
);

export type SortToken = typeof SortToken.Type;

/**
 * Decoders for URL text. A text that breaks a token brand decodes to `None`, so a
 * syntax drops it and never throws on user input.
 */
export const decodeFilterCode = Schema.decodeUnknownOption(FilterCode);
export const decodeFilterValues = Schema.decodeUnknownOption(
  Schema.NonEmptyArray(FilterValueToken)
);
export const decodeSortToken = Schema.decodeUnknownOption(SortToken);

export const FilterToken = Schema.TaggedUnion({
  Terms: { code: FilterCode, values: Schema.NonEmptyArray(FilterValueToken) },
  Range: {
    code: FilterCode,
    min: Schema.NullOr(Schema.Finite),
    max: Schema.NullOr(Schema.Finite),
  },
});

export type FilterToken = typeof FilterToken.Type;

export const FilterTokens = Schema.Struct({
  filters: Schema.Array(FilterToken),
  sort: Schema.NullOr(SortToken),
  page: Schema.Int,
});

export type FilterTokens = typeof FilterTokens.Type;

const tokenKind = (token: FilterToken): number =>
  Match.value(token).pipe(
    Match.tag('Terms', () => 0),
    Match.tag('Range', () => 1),
    Match.exhaustive
  );

const tokenOrder: Order.Order<FilterToken> = (left, right) => {
  const code = Order.String(left.code, right.code);
  return code === 0 ? Order.Number(tokenKind(left), tokenKind(right)) : code;
};

const sameCode = (left: FilterToken, right: FilterToken): boolean => left.code === right.code;

type TermsFilterToken = Extract<FilterToken, { readonly _tag: 'Terms' }>;
type RangeFilterToken = Extract<FilterToken, { readonly _tag: 'Range' }>;

const canonicalTerms = (
  group: ReadonlyArray<FilterToken>,
  code: FilterCode
): ReadonlyArray<FilterToken> => {
  const terms = Array.filter(group, (token): token is TermsFilterToken =>
    Predicate.isTagged(token, 'Terms')
  );
  const values = Array.sort(
    Array.dedupe(Array.flatMap(terms, ({ values: termValues }) => termValues)),
    compareTokens
  );

  return Array.isReadonlyArrayNonEmpty(values) ? [{ _tag: 'Terms', code, values }] : [];
};

const canonicalRange = (group: ReadonlyArray<FilterToken>): ReadonlyArray<FilterToken> => {
  const ranges = Array.filter(
    group,
    (token): token is RangeFilterToken =>
      Predicate.isTagged(token, 'Range') && (token.min !== null || token.max !== null)
  );

  return Array.take(ranges, 1);
};

const canonicalFilterGroup = (
  group: Array.NonEmptyReadonlyArray<FilterToken>
): ReadonlyArray<FilterToken> =>
  Match.value(Array.headNonEmpty(group)).pipe(
    Match.tag('Terms', ({ code }) => canonicalTerms(group, code)),
    Match.tag('Range', () => canonicalRange(group)),
    Match.exhaustive
  );

/** Normalizes URL tokens before a syntax writes them. */
export const canonicalTokens = (tokens: FilterTokens): FilterTokens => {
  const sorted = Array.sort(tokens.filters, tokenOrder);
  const filters = Array.isReadonlyArrayNonEmpty(sorted)
    ? Array.flatMap(Array.groupWith(sorted, sameCode), canonicalFilterGroup)
    : [];

  return { filters, sort: tokens.sort, page: Math.max(1, tokens.page) };
};
